import { useEffect, useState } from "react";
import { Activity, KeyRound, Pencil, Plus, Shield, ShieldCheck, Sliders, Tags, Trash2, Users } from "lucide-react";
import { toast } from "sonner";

import { api, apiError, fmtDate, relativeTime } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { EmptyState, PageHeader, StatusBadge, TableSkeleton } from "@/components/common/Primitives";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { FormGrid, SelectField, TagsField, TextField } from "@/components/forms/Fields";
import { EditUserDialog, RolesTab, UserPrivilegeDialog } from "@/pages/Privileges";

function Profile() {
  const { user, setUser } = useAuth();
  const [form, setForm] = useState({ name: user?.name || "", phone: user?.phone || "", title: user?.title || "" });
  const [prefs, setPrefs] = useState(user?.notification_prefs || { email: true, in_app: true });

  const save = async () => {
    try {
      const { data } = await api.post("/auth/onboard", { ...form, onboarded: true });
      setUser(data);
      toast.success("Profile updated");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const savePrefs = async (next) => {
    setPrefs(next);
    try {
      await api.put("/settings/notifications", { notification_prefs: next });
      toast.success("Notification preferences saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="surface space-y-4 rounded-md p-6">
        <p className="label-xs">Your profile</p>
        <FormGrid>
          <TextField label="Full name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="profile-name-input" />
          <TextField label="Title" value={form.title} onChange={(v) => setForm((f) => ({ ...f, title: v }))} />
          <TextField label="Phone" value={form.phone} onChange={(v) => setForm((f) => ({ ...f, phone: v }))} />
          <div className="space-y-1.5">
            <p className="label-xs">Role</p>
            <StatusBadge value={user?.role_label} />
          </div>
        </FormGrid>
        <p className="text-xs text-slate-500">Signed in as {user?.email}</p>
        <Button onClick={save} data-testid="profile-save-button" className="h-9 rounded-full bg-blue-600 px-5 text-xs hover:bg-blue-500">
          Save profile
        </Button>
      </section>

      <section className="surface space-y-5 rounded-md p-6">
        <p className="label-xs">Notification preferences</p>
        {[["in_app", "In-app notifications", "Interviews, assignments, stage changes and task reminders"],
          ["email", "Email notifications", "Interview confirmations and offer updates by email"]].map(([key, label, desc]) => (
          <div key={key} className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-slate-800">{label}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">{desc}</p>
            </div>
            <Switch
              checked={!!prefs[key]}
              onCheckedChange={(v) => savePrefs({ ...prefs, [key]: v })}
              data-testid={`pref-${key}-switch`}
            />
          </div>
        ))}
      </section>
    </div>
  );
}

function TeamTab() {
  const { meta, can, refreshMeta } = useAuth();
  const [users, setUsers] = useState(null);
  const [open, setOpen] = useState(false);
  const [roleOptions, setRoleOptions] = useState([]);
  const [editing, setEditing] = useState(null);
  const [privFor, setPrivFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", role: "recruiter", password: "Demo@2026", client_id: null, title: "" });
  const canCreate = can("team", "create");
  const canEdit = can("team", "edit");
  const canDelete = can("team", "delete");

  const load = () => api.get("/users").then((r) => setUsers(r.data.items)).catch(() => setUsers([]));
  useEffect(() => {
    load();
    api.get("/roles").then((r) => setRoleOptions(r.data.items.map((x) => ({ value: x.key, label: x.label })))).catch(() => {});
  }, []);

  const invite = async () => {
    if (!form.name.trim() || !form.email.trim()) return toast.error("Name and email are required");
    try {
      await api.post("/auth/invite", form);
      toast.success(`${form.name} invited`);
      setOpen(false);
      setForm({ name: "", email: "", role: "recruiter", password: "Demo@2026", client_id: null, title: "" });
      load();
      refreshMeta();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const toggleActive = async (u) => {
    try {
      await api.patch(`/users/${u.id}`, { is_active: !u.is_active });
      toast.success(`${u.name} ${u.is_active ? "deactivated" : "reactivated"}`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const removeUser = async (u) => {
    if (!window.confirm(`Delete ${u.name}? This cannot be undone.`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success(`${u.name} deleted`);
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-4">
      {canCreate && (
        <div className="flex justify-end">
          <Button onClick={() => setOpen(true)} data-testid="invite-user-button" className="h-9 gap-2 rounded-full bg-blue-600 px-4 text-xs hover:bg-blue-500">
            <Plus className="h-3.5 w-3.5" /> Invite user
          </Button>
        </div>
      )}

      {!users ? (
        <TableSkeleton rows={6} cols={5} />
      ) : (
        <div className="surface overflow-x-auto rounded-md thin-scroll">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 hover:bg-transparent">
                {["Name", "Email", "Role", "Organisation", "Last login", "Status", "Actions"].map((h, i) => (
                  <TableHead key={i} className="label-xs whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {users.map((u) => (
                <TableRow key={u.id} data-testid="user-row" className="border-slate-200 odd:bg-slate-50/80">
                  <TableCell className="text-sm text-slate-900">{u.name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{u.email}</TableCell>
                  <TableCell><StatusBadge value={u.role_label} /></TableCell>
                  <TableCell className="text-xs text-slate-500">{u.client_name || "DIGNIFYD"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{u.last_login_at ? relativeTime(u.last_login_at) : "Never"}</TableCell>
                  <TableCell><StatusBadge value={u.is_active ? "Active" : "On Hold"} /></TableCell>
                  <TableCell>
                    <div className="flex items-center gap-3 whitespace-nowrap">
                      {canEdit && (
                        <>
                          <button onClick={() => setEditing(u)} data-testid="edit-user-button" title="Edit user"
                            className="text-slate-400 hover:text-blue-600"><Pencil className="h-3.5 w-3.5" /></button>
                          <button onClick={() => setPrivFor(u)} data-testid="user-privileges-button" title="Privileges"
                            className="text-slate-400 hover:text-blue-600"><KeyRound className="h-3.5 w-3.5" /></button>
                          <button onClick={() => toggleActive(u)} data-testid="toggle-user-button" className="text-[11px] text-blue-600 hover:text-blue-700">
                            {u.is_active ? "Deactivate" : "Activate"}
                          </button>
                        </>
                      )}
                      {canDelete && (
                        <button onClick={() => removeUser(u)} data-testid="delete-user-button" title="Delete user"
                          className="text-slate-400 hover:text-rose-600"><Trash2 className="h-3.5 w-3.5" /></button>
                      )}
                    </div>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}

      <EditUserDialog user={editing} open={!!editing} onOpenChange={(v) => !v && setEditing(null)} onSaved={load} />
      <UserPrivilegeDialog user={privFor} open={!!privFor} onOpenChange={(v) => !v && setPrivFor(null)} onSaved={load} />

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-md border-slate-200 bg-white">
          <DialogHeader>
            <DialogTitle className="text-base text-slate-900">Invite a user</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <TextField label="Full name" value={form.name} onChange={(v) => setForm((f) => ({ ...f, name: v }))} testid="invite-name-input" required />
            <TextField label="Email" value={form.email} onChange={(v) => setForm((f) => ({ ...f, email: v }))} testid="invite-email-input" required />
            <SelectField label="Role" value={form.role} onChange={(v) => setForm((f) => ({ ...f, role: v }))}
              options={roleOptions.length ? roleOptions : meta?.roles || []} testid="invite-role-select" />
            {form.role === "client" && (
              <SelectField label="Client organisation" value={form.client_id} onChange={(v) => setForm((f) => ({ ...f, client_id: v }))}
                options={(meta?.clients || []).map((c) => ({ value: c.id, label: c.name }))} testid="invite-client-select" />
            )}
            <TextField label="Temporary password" value={form.password} onChange={(v) => setForm((f) => ({ ...f, password: v }))} hint="Share this separately — the invite email never contains it." />
          </div>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setOpen(false)} className="text-slate-500">Cancel</Button>
            <Button onClick={invite} data-testid="invite-submit-button" className="rounded-full bg-blue-600 px-5 hover:bg-blue-500">Send invite</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

function SkillsTab() {
  const { meta, has, refreshMeta } = useAuth();
  const [drafts, setDrafts] = useState({});
  const isAdmin = has("super_admin", "admin");

  useEffect(() => {
    if (meta?.skill_taxonomy) setDrafts(meta.skill_taxonomy);
  }, [meta]);

  const save = async (category) => {
    try {
      await api.post("/settings/skills", { category, skills: drafts[category] });
      toast.success(`${category} taxonomy updated`);
      refreshMeta();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div className="space-y-5">
      {Object.entries(drafts).map(([category, skills]) => (
        <section key={category} className="surface space-y-4 rounded-md p-6" data-testid="skill-category-card">
          <div className="flex items-center justify-between">
            <div>
              <p className="label-xs">Taxonomy</p>
              <h3 className="mt-1 text-base font-medium text-slate-900">{category}</h3>
            </div>
            <span className="font-mono-num text-xs text-slate-500">{skills.length} skills</span>
          </div>
          {isAdmin ? (
            <>
              <TagsField values={skills} onChange={(v) => setDrafts((d) => ({ ...d, [category]: v }))} testid={`skills-${category}-input`} />
              <Button size="sm" onClick={() => save(category)} data-testid={`save-skills-${category}`} className="h-8 rounded-full bg-blue-600 text-xs hover:bg-blue-500">
                Save {category} skills
              </Button>
            </>
          ) : (
            <div className="flex flex-wrap gap-1.5">
              {skills.map((s) => (
                <span key={s} className="rounded border border-slate-200 bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">{s}</span>
              ))}
            </div>
          )}
        </section>
      ))}
    </div>
  );
}

function PlatformTab() {
  const { has } = useAuth();
  const [settings, setSettings] = useState(null);
  const isAdmin = has("super_admin", "admin");

  useEffect(() => {
    api.get("/settings").then((r) => setSettings(r.data)).catch(() => setSettings(false));
  }, []);

  const save = async (next) => {
    setSettings(next);
    try {
      const { id, ...payload } = next;
      await api.put("/settings", payload);
      toast.success("Settings saved");
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  if (!settings) return <TableSkeleton rows={3} cols={2} />;

  return (
    <div className="grid gap-6 lg:grid-cols-2">
      <section className="surface space-y-5 rounded-md p-6">
        <p className="label-xs">Client portal privacy</p>
        {[["hide_candidate_identity_from_clients", "Mask candidate names in the client portal",
           "Clients see an anonymised reference instead of the candidate's name until interview stage."],
          ["hide_candidate_contact_from_clients", "Hide candidate contact details from clients",
           "Email and phone stay internal to the recruitment team."]].map(([key, label, desc]) => (
          <div key={key} className="flex items-start justify-between gap-4">
            <div>
              <p className="text-sm text-slate-800">{label}</p>
              <p className="mt-0.5 text-[11px] text-slate-500">{desc}</p>
            </div>
            <Switch checked={!!settings[key]} disabled={!isAdmin} data-testid={`setting-${key}`}
              onCheckedChange={(v) => save({ ...settings, [key]: v })} />
          </div>
        ))}
      </section>

      <section className="surface space-y-4 rounded-md p-6">
        <p className="label-xs">Delivery defaults</p>
        <TextField label="Company name" value={settings.company_name} onChange={(v) => setSettings((s) => ({ ...s, company_name: v }))} />
        <TextField label="Default SLA (days)" type="number" value={settings.default_sla_days} onChange={(v) => setSettings((s) => ({ ...s, default_sla_days: v }))} />
        {isAdmin && (
          <Button size="sm" onClick={() => save(settings)} data-testid="save-platform-settings" className="h-8 rounded-full bg-blue-600 text-xs hover:bg-blue-500">
            Save defaults
          </Button>
        )}
        <div className="rounded border border-slate-200 bg-slate-50 p-4">
          <p className="flex items-center gap-2 text-xs text-slate-600">
            <Shield className="h-3.5 w-3.5 text-emerald-600" /> Access boundaries in force
          </p>
          <ul className="mt-2 space-y-1 text-[11px] leading-relaxed text-slate-500">
            <li>Clients only ever see their own jobs, submissions and interviews.</li>
            <li>Candidates only see their own profile, applications and documents.</li>
            <li>Interviewers only see interviews assigned to them.</li>
            <li>Internal recruiter notes are never exposed to client or candidate users.</li>
          </ul>
        </div>
      </section>
    </div>
  );
}

function AuditTab() {
  const [items, setItems] = useState(null);
  const [entity, setEntity] = useState("all");

  useEffect(() => {
    setItems(null);
    api.get("/activity", { params: { entity_type: entity !== "all" ? entity : undefined, limit: 150 } })
      .then((r) => setItems(r.data.items))
      .catch(() => setItems([]));
  }, [entity]);

  return (
    <div className="space-y-4">
      <SelectField value={entity} onChange={setEntity} allowAll className="w-56" testid="audit-entity-filter"
        options={["candidate", "job", "client", "application", "interview", "offer", "task", "user", "settings"]}
        placeholder="Entity type" />
      {!items ? (
        <TableSkeleton rows={8} cols={4} />
      ) : items.length === 0 ? (
        <EmptyState icon={Activity} title="No audit entries" />
      ) : (
        <div className="surface overflow-x-auto rounded-md thin-scroll">
          <Table>
            <TableHeader>
              <TableRow className="border-slate-200 hover:bg-transparent">
                {["User", "Action", "Entity", "Related", "When"].map((h) => (
                  <TableHead key={h} className="label-xs whitespace-nowrap">{h}</TableHead>
                ))}
              </TableRow>
            </TableHeader>
            <TableBody>
              {items.map((a) => (
                <TableRow key={a.id} data-testid="audit-row" className="border-slate-200 odd:bg-slate-50/80">
                  <TableCell className="whitespace-nowrap text-xs text-slate-800">{a.user_name}</TableCell>
                  <TableCell className="text-xs text-slate-500">{a.action}</TableCell>
                  <TableCell className="text-xs text-slate-500">{a.entity_type || "—"}</TableCell>
                  <TableCell className="text-xs text-slate-500">{a.entity_name || "—"}</TableCell>
                  <TableCell className="whitespace-nowrap text-xs text-slate-400">{fmtDate(a.created_at)}</TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
      )}
    </div>
  );
}

export default function Settings() {
  const { can } = useAuth();
  const canTeam = can("team", "view");
  const canSettings = can("settings", "view");
  const tabs = [
    ["profile", "Profile", Sliders, true],
    ["team", "Team & users", Users, canTeam],
    ["privileges", "Roles & privileges", ShieldCheck, canTeam],
    ["skills", "Skill taxonomy", Tags, canSettings],
    ["platform", "Platform", Shield, canSettings],
    ["audit", "Audit log", Activity, canTeam],
  ].filter(([, , , show]) => show);

  return (
    <div className="space-y-6">
      <PageHeader meta="Configuration" title="Settings" subtitle="Your profile, team access, role privileges, skill taxonomy and the audit trail." />
      <Tabs defaultValue="profile">
        <TabsList className="flex h-auto w-full flex-wrap justify-start gap-1 border border-slate-200 bg-white p-1">
          {tabs.map(([v, label, Icon]) => (
            <TabsTrigger key={v} value={v} data-testid={`settings-tab-${v}`}
              className="gap-2 rounded px-4 py-1.5 text-xs data-[state=active]:bg-blue-100 data-[state=active]:text-blue-700">
              <Icon className="h-3.5 w-3.5" /> {label}
            </TabsTrigger>
          ))}
        </TabsList>
        <TabsContent value="profile" className="mt-5"><Profile /></TabsContent>
        {canTeam && <TabsContent value="team" className="mt-5"><TeamTab /></TabsContent>}
        {canTeam && <TabsContent value="privileges" className="mt-5"><RolesTab /></TabsContent>}
        {canSettings && <TabsContent value="skills" className="mt-5"><SkillsTab /></TabsContent>}
        {canSettings && <TabsContent value="platform" className="mt-5"><PlatformTab /></TabsContent>}
        {canTeam && <TabsContent value="audit" className="mt-5"><AuditTab /></TabsContent>}
      </Tabs>
    </div>
  );
}
